import { useContext, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { Box, Modal, Typography, Grid, Button, InputAdornment, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MuiValidSelect from 'src/components/input/MuiValidSelect';
import MuiValidTextField from 'src/components/input/MuiValidTextField';
import { useAPI } from 'src/services/api';
import ToastContext from 'src/store/toast-context';
import RemoveModal from 'src/components/modal/RemoveModal';
import AssetSchema from 'src/components/schemas/assetSchema';
import ModalStyle from 'src/components/modal/ModalStyle';
import AuthContext from 'src/store/auth-context';
import FormObserver from 'src/components/FormObserver';
import AssetTableContext from 'src/store/asset-table-context';
import { useMediaQuery } from 'react-responsive';
import { pick } from 'lodash';
import WholeNumberInput from 'src/components/input/WholeNumberInput';
import MuiCheckbox from 'src/components/input/MuiCheckbox';

const AssetModal = () => {
  const { assetModal, assetInfo } = useContext(AssetTableContext);
  const { updating, assets, initialValues, propDescriptions, propCode, updateAssets } = assetInfo;
  const { assetModalController, resetAssetModal } = assetModal;

  const api = useAPI();
  const { handleError, success, error } = useContext(ToastContext);
  const { backdoor } = useContext(AuthContext);
  const [descSelected, setDescSelected] = useState(false);
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' });

  const isExistingAsset = (updating, row) => {
    return (
      (updating.addDel === 'O' && row.itemNo === updating.itemNo) ||
      (row.recno === updating.recno && row.recno !== 0)
    );
  };

  const removeNewAsset = async () => {
    // Newly added asset needs to be removed from Schedule_B
    await api.delete(`/assets/delete/${updating.recno}`);
    updateAssets(assets.filter((asset) => asset.recno !== updating.recno));
  };

  const removeOldAsset = async () => {
    // eslint-disable-next-line no-unused-vars
    const { marketVal, rate, exemptFlag, createdAt, ...rest } = updating;
    const { data } = await api.patch(
      `/assets/markDelete/${updating.addDel === 'O' ? updating.itemNo : updating.recno}`,
      {
        ...rest,
      }
    );
    updateRow(updating, {
      addDel: 'D',
      recno: data.recno,
      marketVal: data.marketVal,
      rate: parseFloat(data.rate),
    });
  };

  const updateExistingAsset = async (values) => {
    // Asset needs to be put in Schedule_B with updated values
    // eslint-disable-next-line no-unused-vars
    const { marketVal, rate, exemptFlag, recno, createdAt, ...rest } = updating;

    if (updating.addDel === 'O') {
      const { data } = await api.patch(`/assets/${updating.itemNo}`, {
        ...rest,
        ...values,
      });
      updateRow(values, {
        addDel: 'C',
        recno: data.recno,
        marketVal: data.marketVal,
        rate: parseFloat(data.rate),
      });
      // Assets is in Schedule_B and needs to have values updated
    } else {
      const { data } = await api.patch(`/assets/${updating.recno}`, {
        ...rest,
        ...values,
      });
      updateRow(values, {
        marketVal: data.marketVal,
        rate: parseFloat(data.rate),
        recno: data.recno,
      });
    }
  };

  const addNewAsset = async (values) => {
    const { data } = await api.post('/assets/', values);
    let pickedData = pick(data, [
      'addDel',
      'assetId',
      'createdAt',
      'exemptFlag',
      'itemDescr',
      'itemNo',
      'lesseeAddr',
      'lesseeCity',
      'lesseeName',
      'marketVal',
      'propCode',
      'purchaseCost',
      'quantity',
      'rate',
      'recno',
      'taxYear',
      'yearAcq',
    ]);
    updateAssets([pickedData, ...assets]);
  };

  const updateRow = (values, args) => {
    updateAssets(
      assets.map((row) => {
        if (isExistingAsset(updating, row)) {
          return { ...row, ...values, ...args };
        }
        return row;
      })
    );
  };

  const handleRemove = async () => {
    assetModalController.closeModal();
    try {
      if (updating.addDel === 'A') {
        await removeNewAsset();
        success('Successfully removed asset');
      }
      if (updating.addDel === 'D') {
        error(`Asset has already been marked for deletion`);
      }
      if (updating.addDel === 'O' || updating.addDel === 'C') {
        await removeOldAsset();
        success('Successfully marked asset for deletion');
      }

      resetAssetModal();
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  const handleSubmit = async (values) => {
    // eslint-disable-next-line no-unused-vars
    const { isLeased, ...apiValues } = values;
    const valuesForApi = {
      ...apiValues,
      purchaseCost: Math.round(apiValues.purchaseCost).toString(),
    };

    assetModalController.closeModal();
    try {
      if (updating) {
        await updateExistingAsset(valuesForApi);
        success('Successfully updated asset');
      } else {
        await addNewAsset(valuesForApi);
        success('Successfully added asset');
      }
    } catch (e) {
      console.error(e);
      handleError(e);
    }
  };

  return (
    <>
      <Formik
        onSubmit={(values, { resetForm }) => {
          resetForm();
          resetAssetModal();
          handleSubmit(values);
        }}
        validationSchema={AssetSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {({ resetForm, setFieldValue, dirty, values }) => {
          return (
            <Modal
              open={assetModalController.open}
              onClose={() => {
                resetForm();
                resetAssetModal();
                setDescSelected(false);
              }}
            >
              <Box sx={ModalStyle}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: '1rem',
                  }}
                >
                  <Typography variant="h5" sx={{ textAlign: 'center' }}>
                    {updating ? 'Update' : 'Add'} Asset
                  </Typography>
                  <IconButton
                    aria-label="close"
                    size="small"
                    onClick={() => {
                      resetForm();
                      resetAssetModal();
                      setDescSelected(false);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </Box>
                <Form>
                  <FormObserver />
                  <Grid container rowSpacing={4} columnSpacing={{ xs: 2, sm: 4, md: 6 }}>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidSelect}
                        label="Description Selector"
                        name="descrSelector"
                        itemValues={propDescriptions?.map((item) => item.classCode)}
                        itemLabels={propDescriptions?.map((item) => item.ppTypeDescr)}
                        onChange={(e) => {
                          setFieldValue(
                            'propCode',
                            propCode.find((code) => code.ppsType === e.target.value)?.ppsType ?? ''
                          );
                          setDescSelected(
                            e.target.value === 'Item Not Listed' ? false : !!e.target.value
                          );
                        }}
                        native
                        variant="standard"
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Item Description"
                        name="itemDescr"
                        variant="standard"
                        inputProps={{ maxLength: 200 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidSelect}
                        disabled={descSelected}
                        label="Property Code"
                        name="propCode"
                        itemValues={propCode?.map((item) => item.ppsType)}
                        itemLabels={propCode?.map((item) => item.ppsDescr)}
                        native
                        variant="standard"
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        label="Asset Number"
                        name="assetId"
                        variant="standard"
                        inputProps={{ maxLength: 20 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={WholeNumberInput}
                        label="Year Acquired"
                        name="yearAcq"
                        placeholder="YYYY"
                        variant="standard"
                        inputProps={{ maxLength: 4 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={MuiValidTextField}
                        onChange={(e) => {
                          e.preventDefault();

                          // Regular expression to match numbers with up to two decimal places
                          const reg = /^\d*\.?\d{0,2}$/;

                          // Get current value of the input field
                          const currentValue = e.target.value;

                          if (!reg.test(currentValue)) {
                            // Prevent input if it doesn't match the regex
                            e.preventDefault();
                          } else {
                            // Log the value if it's valid
                            setFieldValue('purchaseCost', e.target.value);
                          }
                        }}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography component="span">
                              Purchase Cost <strong style={{ fontWeight: 'bold' }}>(EACH)</strong>
                            </Typography>
                            <Tooltip
                              title="Enter the cost per individual item. The total cost will be calculated by multiplying this value by the Quantity entered."
                              arrow
                              placement="right"
                            >
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: 16,
                                  marginLeft: 0.5,
                                  color: 'action.active',
                                  cursor: 'pointer',
                                }}
                              />
                            </Tooltip>
                          </Box>
                        }
                        name="purchaseCost"
                        variant="standard"
                        inputProps={{ maxLength: 13 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        component={WholeNumberInput}
                        label="Quantity"
                        name="quantity"
                        variant="standard"
                        inputProps={{ maxLength: 4 }}
                        InputLabelProps={{ shrink: true }}
                      ></Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        component={MuiCheckbox}
                        name="isLeased"
                        label="This item is currently being leased"
                        onChange={(e) => {
                          setFieldValue('isLeased', e.target.checked);
                          if (!e.target.checked) {
                            setFieldValue('lesseeName', '');
                            setFieldValue('lesseeAddr', '');
                            setFieldValue('lesseeCity', '');
                          }
                        }}
                      />
                    </Grid>
                    {values.isLeased && (
                      <>
                        <Grid item xs={12} md={12}>
                          <Typography sx={{ paddingTop: 3 }}>
                            Leasing Company Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            component={MuiValidTextField}
                            label="Leased to"
                            name="lesseeName"
                            variant="standard"
                            inputProps={{ maxLength: 70 }}
                            InputLabelProps={{ shrink: true }}
                          ></Field>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            component={MuiValidTextField}
                            label="Lessee Address (Optional Apt/Unit Number)"
                            name="lesseeAddr"
                            variant="standard"
                            inputProps={{ maxLength: 80 }}
                            InputLabelProps={{ shrink: true }}
                            helpText="a person who holds the lease of a property; a tenant."
                          ></Field>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            component={MuiValidTextField}
                            label="Lessee City"
                            name="lesseeCity"
                            variant="standard"
                            inputProps={{ maxLength: 40 }}
                            InputLabelProps={{ shrink: true }}
                            helpText="a person who holds the lease of a property; a tenant."
                          ></Field>
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: '1rem',
                    }}
                  >
                    <Box sx={{ display: 'flex', marginLeft: 'auto', gap: '0.5rem' }}>
                      {updating && (
                        <RemoveModal itemTitle={initialValues.itemDescr} onRemove={handleRemove} />
                      )}

                      <Button
                        variant="outlined"
                        onClick={() => {
                          resetForm();
                          resetAssetModal();
                        }}
                        sx={{ color: 'gray' }}
                      >
                        Close
                      </Button>
                      <Button
                        type="submit"
                        variant="outlined"
                        color="primary"
                        disabled={!dirty || backdoor}
                        data-cy="save-asset-btn"
                      >
                        {isDesktop ? 'Confirm and Save' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                </Form>
              </Box>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

export default AssetModal;
